import { LoadProgress } from '../types'

/**
 * Progress reporting for model loading.
 *
 * The expensive steps here — OCCT's STEP tessellation above all — are synchronous:
 * once they start they hold the main thread until they finish, so React cannot
 * re-render and any JS-driven animation stops dead. Two things follow.
 *
 * First, a phase must be announced BEFORE the work it describes begins, and the
 * browser must be given the chance to paint that announcement — hence the frame wait
 * below. Without it every phase would be reported and then instantly superseded, and
 * the user would see only the last one.
 *
 * Second, the spinner itself must animate through a blocked main thread, which means
 * transform/opacity only: those are driven by the compositor and keep running. See
 * LoadingOverlay.
 */
export type ProgressReporter = (label: string, percent: number) => Promise<void>

/** Resolve after the browser has had a chance to paint. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0)
      return
    }
    // rAF fires before the paint; the timeout lands just after it.
    requestAnimationFrame(() => setTimeout(resolve, 0))
  })
}

export function makeReporter(onProgress?: (progress: LoadProgress) => void): ProgressReporter {
  if (!onProgress) return async () => {}
  return async (label, percent) => {
    onProgress({ label, percent })
    await nextPaint()
  }
}
